import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Editor from './components/Editor';
import WordEditor from './components/WordEditor';
import AIAssistant from './components/AIAssistant';
import SidebarChat from './components/SidebarChat';
import APISettings from './components/APISettings';
import RecentFiles from './components/RecentFiles';
import HelpModal from './components/HelpModal';
import TutorialModal from './components/TutorialModal';
import SaveAsModal from './components/SaveAsModal';
import { ThemeProvider } from './context/ThemeContext';
import AdminModal from './components/AdminModal';
import AdminDashboard from './components/AdminDashboard';
import ChargeModal from './components/ChargeModal';
import LoginModal from './components/LoginModal';
import pointsService from './services/pointsService';
import contextService from './services/contextService';
import fileService from './services/fileService';
import authService from './services/authService';
import supabaseService from './services/supabaseService';

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background: ${p => p.theme.colors.background};
  background-image: ${p => p.theme.colors.backgroundGradient};
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  background: transparent;
  padding: 8px;
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.radius.lg};
  margin: 8px;
  overflow: hidden;
  box-shadow: ${p => p.theme.shadow.md};
`;

const SupportFab = styled.button`
  position: fixed;
  left: 16px;
  bottom: 16px;
  z-index: 1000;
  background: #0A84FF;
  color: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 10px 14px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 8px 20px rgba(10,132,255,0.35);
  cursor: pointer;
`;

function WordApp() {
  const DEBUG = false; // 로그 제어 플래그
  const [isPreviewGuest, setIsPreviewGuest] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [content, setContent] = useState('');
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);
  const [isSidebarChatOpen, setIsSidebarChatOpen] = useState(false);
  const [isSidebarChatMinimized, setIsSidebarChatMinimized] = useState(false);
  const [aiMode, setAiMode] = useState('write'); // 'write', 'edit', 'question'
  const [isAPISettingsOpen, setIsAPISettingsOpen] = useState(false);
  const [isRecentFilesOpen, setIsRecentFilesOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [isSaveAsOpen, setIsSaveAsOpen] = useState(false);
  const [recentFiles, setRecentFiles] = useState([]);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewMode, setPreviewMode] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedText, setSelectedText] = useState(null);
  const [account, setAccount] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ai_agent_account') || '{}'); } catch { return {}; }
  });
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isChargeOpen, setIsChargeOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ active: false, percent: 0, page: 0, totalPages: 0, message: '' });
  const autosaveTimerRef = useRef(null);
  const lastAutosaveHashRef = useRef('');
  const autosaveNameRef = useRef('');
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);

  // 자동 저장된 내용 불러오기
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    // 관리자 페이지 체크 (최우선)
    if (params.get('admin') === '1') {
      if (DEBUG) console.log('관리자 모드 진입');
      setIsAdminOpen(true);
      return; // 관리자 모드일 때는 다른 모든 로직 건너뛰기
    }
    
    // 로그인 모달 자동 열기 (PayApp 결제 후)
    if (params.get('login') === '1') {
      if (DEBUG) console.log('PayApp 결제 후 로그인 모달 자동 열기');
      setTimeout(() => {
        setIsLoginOpen(true);
      }, 1000);
    }
    
    
    // 초기 포인트 로드
    const loadInitialPoints = () => {
      try {
        const session = authService.getSession();
        if (session?.user?.email) {
          // 로그인 사용자: Supabase에서 포인트 로드
          supabaseService.getUserData(session.user.email).then(userData => {
            if (userData?.points !== undefined) {
              setAccount(prev => ({ ...prev, points: userData.points }));
              localStorage.setItem('ai_agent_points_v1', String(userData.points));
            }
          }).catch(() => {
            // 실패 시 현재 포인트 사용
            const currentPoints = pointsService.getCurrentBalance();
            setAccount(prev => ({ ...prev, points: currentPoints }));
          });
        } else {
          // 게스트 사용자 포인트 로드
          const guestPoints = pointsService.getCurrentBalance();
          setAccount(prev => ({ ...prev, points: guestPoints }));
        }
      } catch (error) {
        console.error('초기 포인트 로드 실패:', error);
        const currentPoints = pointsService.getCurrentBalance();
        setAccount(prev => ({ ...prev, points: currentPoints }));
      }
    };
    
    loadInitialPoints();
    
    // 결제 완료 시 포인트 업데이트 이벤트 리스너
    const handlePointsUpdate = (event) => {
      if (DEBUG) console.log('🎯 App.js: 포인트 업데이트 이벤트 수신:', event.detail);
      const newPoints = event.detail.points;
      if (DEBUG) console.log('🎯 App.js: 현재 account 상태:', account);
      if (DEBUG) console.log('🎯 App.js: 새로운 포인트:', newPoints);
      
      setAccount(prev => {
        const updated = { ...prev, points: newPoints };
        if (DEBUG) console.log('🎯 App.js: account 상태 업데이트:', prev, '→', updated);
        return updated;
      });
      
      localStorage.setItem('ai_agent_points_v1', String(newPoints));
      if (DEBUG) console.log('🎯 App.js: localStorage 업데이트 완료');
    };
    
    window.addEventListener('pointsUpdated', handlePointsUpdate);
    
    return () => {
      window.removeEventListener('pointsUpdated', handlePointsUpdate);
    };
    
    // SERVICE_ROLE_KEY를 사용한 빠른 포인트 새로고침 (로그인 사용자만)
    const refreshPoints = async () => {
      try {
        if (DEBUG) console.log('🔄 포인트 새로고침 시작...');
        const session = authService.getSession();
        if (DEBUG) console.log('📧 authService 사용자:', session?.user?.email);
        
        // authService가 실패하면 localStorage에서 직접 가져오기
        let userEmail = session?.user?.email;
        if (!userEmail) {
          const accountData = JSON.parse(localStorage.getItem('ai_agent_account') || '{}');
          userEmail = accountData.email;
          if (DEBUG) console.log('📧 localStorage 사용자:', userEmail);
        }
        
        if (userEmail) {
          // SERVICE_ROLE_KEY를 사용한 빠른 조회
          const apiUrl = '/api/supabase-admin?path=users%3Femail%3Deq.' + encodeURIComponent(userEmail) + '%26select%3D*&method=GET&api=rest';
          if (DEBUG) console.log('🌐 API 호출:', apiUrl);
          
          const response = await fetch(apiUrl);
          if (DEBUG) console.log('📡 API 응답 상태:', response.status);
          
          if (response.ok) {
            const userDataArray = await response.json();
            if (DEBUG) console.log('📊 서버 데이터:', userDataArray);
            
            if (userDataArray && userDataArray.length > 0) {
              const userData = userDataArray[0];
              if (DEBUG) console.log('👤 사용자 정보:', userData);
              
              if (userData.points !== undefined) {
                const currentPoints = JSON.parse(localStorage.getItem('ai_agent_account') || '{}').points;
                if (DEBUG) console.log('💰 현재 포인트:', currentPoints, '→ 서버 포인트:', userData.points);
                
                // 포인트가 실제로 변경된 경우에만 업데이트
                if (currentPoints !== userData.points) {
                  if (DEBUG) console.log('✨ 포인트 변경 감지! 업데이트 중...');
                  setAccount(prev => ({ ...prev, points: userData.points }));
                  localStorage.setItem('ai_agent_points_v1', String(userData.points));
                  if (DEBUG) console.log('✅ 포인트 업데이트 완료:', userData.points);
                  
                  // 포인트 업데이트 이벤트 발생
                  window.dispatchEvent(new CustomEvent('pointsUpdated', { 
                    detail: { points: userData.points } 
                  }));
                  if (DEBUG) console.log('📢 포인트 업데이트 이벤트 발생');
                } else {
                  if (DEBUG) console.log('⏸️ 포인트 변경 없음');
                }
              }
            } else {
              if (DEBUG) console.log('❌ 사용자 데이터 없음');
            }
          } else {
            if (DEBUG) console.error('❌ API 호출 실패:', response.status);
          }
        } else {
          if (DEBUG) console.log('❌ 로그인된 사용자 없음');
        }
      } catch (error) {
        if (DEBUG) console.error('💥 포인트 새로고침 실패:', error);
      }
    };
    
    const interval = setInterval(() => {
      refreshPoints();
    }, 5000); // 5초마다 체크
    
    return () => clearInterval(interval);
    
    if (params.get('preview') === '1') {
      setIsPreviewGuest(true);
      setAiMode('write');
      setIsAIAssistantOpen(false);
      setIsSidebarChatOpen(false);
      // 게스트 강제: 관리자/로그인 표시 막기
      localStorage.removeItem('ai_agent_account');
      // 미리보기용 문서 로드 (읽기 전용 느낌)
      fetch('/preview.md')
        .then(r => r.text())
        .then(t => { setContent(t); contextService.updateDocumentContent(t); })
        .catch(() => {});
    }
    const autoSavedContent = contextService.loadAutoSavedDocument();
    if (autoSavedContent) {
      setContent(autoSavedContent);
    }
    
    // 최근 파일 목록 불러오기
    setRecentFiles(fileService.getRecentFiles());
    // 초기 포인트를 상태에 반영
    setAccount(prev => ({ ...prev, points: pointsService.getCurrentBalance() }));
    
    // 첫 방문자 체크 (미리보기 모드가 아닐 때만)
    if (params.get('preview') !== '1') {
      const tutorialCompleted = localStorage.getItem('tutorialCompleted');
      if (!tutorialCompleted) {
        // 2초 후 튜토리얼 표시 (페이지 로딩 완료 후)
        const timer = setTimeout(() => {
          setIsTutorialOpen(true);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  // PDF 진행 이벤트 리스너
  useEffect(() => {
    const onStart = () => setPdfProgress({ active: true, percent: 0, page: 0, totalPages: 0, message: 'PDF 준비 중...' });
    const onProgress = (e) => {
      const { percent = 0, page = 0, totalPages = 0 } = (e?.detail || {});
      setPdfProgress(prev => ({ active: true, percent, page, totalPages, message: totalPages ? `${page}/${totalPages} 페이지 처리 중` : 'PDF 처리 중...' }));
    };
    const onDone = (e) => {
      const hasError = !!e?.detail?.error;
      setPdfProgress({ active: false, percent: hasError ? 0 : 100, page: 0, totalPages: 0, message: hasError ? '저장 실패' : '완료' });
    };
    window.addEventListener('pdfStart', onStart);
    window.addEventListener('pdfProgress', onProgress);
    window.addEventListener('pdfDone', onDone);
    return () => {
      window.removeEventListener('pdfStart', onStart);
      window.removeEventListener('pdfProgress', onProgress);
      window.removeEventListener('pdfDone', onDone);
    };
  }, []);

  const handleSave = useCallback(async () => {
    if (isPreviewGuest) return; // 미리보기에서는 저장 금지
    setIsSaveAsOpen(true);
  }, [isPreviewGuest]);

  // Ctrl+S용 빠른 수동 저장 (최근 파일에 스냅샷)
  const handleManualSave = useCallback(() => {
    if (isPreviewGuest) return;
    const timestamp = new Date();
    const name = `수동저장_${timestamp.toLocaleDateString()}_${timestamp.toLocaleTimeString()}.md`;
    fileService.addRecentFile({
      name,
      path: name,
      type: 'manual',
      savedAt: timestamp.toISOString(),
      content
    });
    setRecentFiles(fileService.getRecentFiles());
  }, [content, isPreviewGuest]);

  const handleSaveAsSelect = async (format) => {
    try {
      let result;
      if (format === 'pdf') {
        result = await fileService.saveAsPDF(content);
      } else {
        result = await fileService.saveAsWord(content);
      }
      if (result?.success) {
        setRecentFiles(fileService.getRecentFiles());
      }
    } catch (e) {
      if (DEBUG) console.error('파일 저장 실패:', e);
    } finally {
      setIsSaveAsOpen(false);
    }
  };

  const ensureModelSelected = () => {
    const model = localStorage.getItem('oneline_model');
    if (!model) {
      setIsAPISettingsOpen(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    // Electron 메뉴 이벤트 리스너 설정
    if (window.electronAPI) {
      const { 
        onMenuNewDocument, 
        onMenuOpenFile, 
        onMenuSaveDocument,
        onMenuAIWrite,
        onMenuAIEdit,
        onMenuAIQuestion
      } = window.electronAPI;

      onMenuNewDocument(() => {
        setCurrentFile(null);
        setContent('');
      });

      onMenuOpenFile((event, { filePath, content }) => {
        setCurrentFile(filePath);
        setContent(content);
      });

      onMenuSaveDocument(() => {
        handleSave();
      });

      onMenuAIWrite(() => {
        if (!ensureModelSelected()) return;
        setAiMode('write');
        setIsAIAssistantOpen(true);
      });

      onMenuAIEdit(() => {
        if (!ensureModelSelected()) return;
        setAiMode('edit');
        setIsAIAssistantOpen(true);
      });

      onMenuAIQuestion(() => {
        if (!ensureModelSelected()) return;
        setAiMode('question');
        setIsAIAssistantOpen(true);
      });
    }
  }, [handleSave]);

  // 전역 Undo/Redo: 텍스트영역 외에서의 변경(미리보기 적용, 파일 로드 등)에 대해 Ctrl+Z/Y 지원
  useEffect(() => {
    const onKey = (e) => {
      const target = e.target;
      const isTextInput = target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.isContentEditable);
      // 에디터 내부 네이티브 Undo는 방해하지 않음
      if (isTextInput) return;

      // Ctrl+Z (또는 Cmd+Z)
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        if (undoStackRef.current.length > 0) {
          e.preventDefault();
          const prev = undoStackRef.current.pop();
          redoStackRef.current.push(content);
          setContent(prev);
          contextService.updateDocumentContent(prev);
        }
      }
      // Ctrl+Y 또는 Ctrl+Shift+Z (Redo)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || e.key === 'Y' || (e.shiftKey && (e.key === 'z' || e.key === 'Z')))) {
        if (redoStackRef.current.length > 0) {
          e.preventDefault();
          const next = redoStackRef.current.pop();
          undoStackRef.current.push(content);
          setContent(next);
          contextService.updateDocumentContent(next);
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [content]);

  const handleContentChange = (newContent) => {
    setContent(newContent);
    // 컨텍스트 서비스에 문서 내용 업데이트
    contextService.updateDocumentContent(newContent);
  };

  // 수동 입력/붙여넣기 포함 모든 편집 자동 저장 → 최근 파일에 주기적 스냅샷
  useEffect(() => {
    if (isPreviewGuest) return; // 미리보기에서는 저장 금지
    // 세션 기반 자동저장 파일명(고정) - 동일 이름으로 덮어써서 스팸 방지
    if (!autosaveNameRef.current) {
      const sessionId = contextService.getCurrentSession()?.id || 'session';
      autosaveNameRef.current = `자동저장_${sessionId}.md`;
    }

    // 내용이 매우 짧으면 자동저장 생략
    if (!content || content.trim().length < 5) return;

    // 동일 내용 반복 저장 방지 (간단 해시)
    const hash = String(content.length) + '_' + String(content.charCodeAt(0) || 0) + '_' + String(content.charCodeAt(content.length - 1) || 0);
    if (lastAutosaveHashRef.current === hash) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      try {
        fileService.addRecentFile({
          name: autosaveNameRef.current,
          path: autosaveNameRef.current,
          type: 'autosave',
          savedAt: new Date().toISOString(),
          content: content
        });
        setRecentFiles(fileService.getRecentFiles());
        lastAutosaveHashRef.current = hash;
      } catch (e) {
        console.warn('자동저장 실패:', e);
      }
    }, 1500); // 입력 멈춘 뒤 1.5초 후 저장

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [content, isPreviewGuest]);

  const handleAIResponse = (response, mode) => {
    if (DEBUG) console.log('handleAIResponse 호출:', {
      response: response?.substring(0, 100) + '...',
      mode,
      selectedRange,
      selectedText: selectedText?.substring(0, 50) + '...'
    });
    setAccount(prev => ({ ...prev, points: pointsService.getCurrentBalance() }));

    // 선택 없음 + 수정 모드(JSON Patch)
    if (mode === 'edit' && !selectedRange) {
      const tryParseJson = (text) => {
        try {
          const trimmed = (text || '').trim().replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
          return JSON.parse(trimmed);
        } catch (_) { return null; }
      };
      const patches = tryParseJson(response);
      if (Array.isArray(patches)) {
        window.__aiLastPatches = patches;
        // 미리보기 내용이 있으면 그것을 기준으로, 없으면 현재 내용을 기준으로 패치 적용
        const baseContent = previewContent && previewMode ? previewContent : content;
        setPreviewContent(applyPatches(baseContent, patches));
        setPreviewMode('edit-patch'); // edit와 구분
        setIsAIAssistantOpen(false);
        return;
      }
    }

    // write 모드: 기존 내용이 있으면 미리보기에 기존 내용 + 새 내용을 모두 표시
    if (mode === 'write' && content && content.trim().length > 0) {
      const combinedContent = content + (content.trim().endsWith('\n') ? '' : '\n\n') + response;
      setPreviewContent(combinedContent);
      setPreviewMode(mode);
      setIsAIAssistantOpen(false);
      return;
    }

    // 기본: 미리보기 경로 유지
    setPreviewContent(response);
    setPreviewMode(mode);
    setIsAIAssistantOpen(false);
  };

  // 기존 patch계산 함수 추출
  const applyPatches = (source, ops) => {
    let next = source;
    for (const op of ops) {
      if (!op || typeof op !== 'object') continue;
      const target = op.target;
      if (!target || typeof target !== 'string' || target.length === 0) continue;
      const idx = next.indexOf(target);
      if (idx === -1) continue;
      if (op.type === 'replace') {
        const before = next.slice(0, idx);
        const after = next.slice(idx + target.length);
        next = before + (op.replacement ?? '') + after;
      } else if (op.type === 'insert_after') {
        const insertPos = idx + target.length;
        next = next.slice(0, insertPos) + (op.content ?? '') + next.slice(insertPos);
      } else if (op.type === 'insert_before') {
        const insertPos = idx;
        next = next.slice(0, insertPos) + (op.content ?? '') + next.slice(insertPos);
      }
    }
    return next;
  };

  const handleApplyPreview = () => {
    console.log('handleApplyPreview 호출:', {
      previewContent: previewContent?.substring(0, 100) + '...',
      previewMode,
      selectedRange,
      selectedText: selectedText?.substring(0, 50) + '...'
    });

    if (previewContent && previewMode) {
      if (previewMode === 'write') {
        // 글 작성 모드: previewContent에 이미 기존 내용이 포함되어 있으므로 그대로 사용
        setContent(prev => {
          // 히스토리 스냅샷 (비-텍스트 변경 되돌리기용)
          undoStackRef.current.push(prev);
          redoStackRef.current = [];
          // previewContent에 이미 기존 내용 + 새 내용이 모두 포함되어 있음
          const newContent = previewContent;
          contextService.updateDocumentContent(newContent);
          
          // 새로운 내용이 적용되면 최근 파일에 추가
          if (newContent.trim().length >= 50) {
            const timestamp = new Date().toISOString();
            const fileName = `AI작성_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.docx`;
            
            fileService.addRecentFile({
              name: fileName,
              path: `ai-written-${timestamp}`,
              type: 'docx',
              savedAt: timestamp,
              content: newContent
            });
            
            // 최근 파일 목록 업데이트
            setRecentFiles(fileService.getRecentFiles());
          }
          
          return newContent;
        });
      } else if (previewMode === 'edit') {
        // 수정 모드: 선택된 부분만 교체
        if (selectedRange && selectedText) {
          if (DEBUG) console.log('부분 수정 실행:', {
            selectedRange,
            selectedTextLength: selectedText.length,
            previewContentLength: previewContent.length
          });
          
          setContent(prev => {
            // 히스토리 스냅샷
            undoStackRef.current.push(prev);
            redoStackRef.current = [];
            const beforeSelection = prev.substring(0, selectedRange.start);
            const afterSelection = prev.substring(selectedRange.end);
            const newContent = beforeSelection + previewContent + afterSelection;
            
            if (DEBUG) console.log('부분 수정 결과:', {
              beforeLength: beforeSelection.length,
              previewLength: previewContent.length,
              afterLength: afterSelection.length,
              totalLength: newContent.length
            });
            
            contextService.updateDocumentContent(newContent);
            
            // 새로운 내용이 적용되면 최근 파일에 추가
            if (newContent.trim().length >= 50) {
              const timestamp = new Date().toISOString();
              const fileName = `AI생성_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.docx`;
              
              fileService.addRecentFile({
                name: fileName,
                path: `ai-generated-${timestamp}`,
                type: 'docx',
                savedAt: timestamp,
                content: newContent
              });
              
              // 최근 파일 목록 업데이트
              setRecentFiles(fileService.getRecentFiles());
            }
            
            return newContent;
          });
        } else {
          if (DEBUG) console.log('전체 수정 실행 (selectedRange 또는 selectedText 없음)');
          // 선택된 부분이 없으면 전체 교체
          setContent(prev => {
            // 히스토리 스냅샷
            undoStackRef.current.push(prev);
            redoStackRef.current = [];
            contextService.updateDocumentContent(previewContent);
            
            // 새로운 내용이 적용되면 최근 파일에 추가
            if (previewContent.trim().length >= 50) {
              const timestamp = new Date().toISOString();
              const fileName = `AI생성_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.docx`;
              
              fileService.addRecentFile({
                name: fileName,
                path: `ai-generated-${timestamp}`,
                type: 'docx',
                savedAt: timestamp,
                content: previewContent
              });
              
              // 최근 파일 목록 업데이트
              setRecentFiles(fileService.getRecentFiles());
            }
            
            return previewContent;
          });
        }
      } else if (previewMode === 'edit-patch') {
        // 히스토리 스냅샷
        undoStackRef.current.push(content);
        redoStackRef.current = [];
        setContent(previewContent);
        contextService.updateDocumentContent(previewContent);
        // 최근 파일 저장 등은 기존대로
        if (previewContent.trim().length >= 50) {
          const timestamp = new Date().toISOString();
          const fileName = `AI수정_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.docx`;
          fileService.addRecentFile({ name: fileName, path: `ai-edited-${timestamp}`, type: 'docx', savedAt: timestamp, content: previewContent });
          setRecentFiles(fileService.getRecentFiles());
        }
        setPreviewContent(null);
        setPreviewMode(null);
        setSelectedRange(null);
        setSelectedText(null);
        return;
      }
    }
    // 미리보기 초기화
    setPreviewContent(null);
    setPreviewMode(null);
    setSelectedRange(null);
    setSelectedText(null);
  };

  const handleRejectPreview = () => {
    // 미리보기 취소
    setPreviewContent(null);
    setPreviewMode(null);
    setSelectedRange(null);
    setSelectedText(null);
    if (DEBUG) console.log('미리보기 취소 - 모든 상태 초기화');
  };

  const handleStartChat = (selectedText, selectionRange) => {
    if (DEBUG) console.log('App handleStartChat 호출:', {
      selectedText: selectedText.substring(0, 50) + '...',
      selectionRange,
      currentAiMode: aiMode
    });
    
    // 선택된 텍스트와 범위 저장
    setSelectedText(selectedText);
    setSelectedRange(selectionRange);
    
    // 수정 모드로 설정하고 사이드바 열기
    setAiMode('edit');
    setIsSidebarChatOpen(true);
    setIsSidebarChatMinimized(false);
    
    if (DEBUG) console.log('사이드바 열기 완료:', {
      isSidebarChatOpen: true,
      aiMode: 'edit',
      selectedText: selectedText.substring(0, 50) + '...'
    });
  };

  const handleOpenFile = async () => {
    if (isPreviewGuest) return; // 미리보기에서는 파일 열기 금지
    // 웹에서는 파일 입력 다이얼로그 시뮬레이션
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.md,.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setCurrentFile(file.name);
          setContent(e.target.result);
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleToggleAI = (mode) => {
    if (isPreviewGuest) return; // 미리보기에서는 AI 호출 금지
    if (!ensureModelSelected()) return;
    setAiMode(mode);
    setIsAIAssistantOpen(true);
  };

  const onToggleSidebarChat = () => {
    if (!isSidebarChatOpen) {
      if (!isPreviewGuest && !ensureModelSelected()) return;
    }
    setIsSidebarChatOpen(!isSidebarChatOpen);
    if (!isSidebarChatOpen) {
      setIsAIAssistantOpen(false); // 사이드바 채팅이 열리면 팝업 닫기
    }
  };

  const onToggleAIAssistant = () => {
    setIsAIAssistantOpen(!isAIAssistantOpen);
    if (!isAIAssistantOpen) {
      setIsSidebarChatOpen(false); // 팝업이 열리면 사이드바 채팅 닫기
    }
  };

  const onMinimizeSidebarChat = () => {
    setIsSidebarChatMinimized(!isSidebarChatMinimized);
  };

  const onCloseSidebarChat = () => {
    setIsSidebarChatOpen(false);
  };

  const onModeChange = (newMode) => {
    setAiMode(newMode);
  };

  const onNewConversation = () => {
    // 새 대화 시작 (문서 내용은 유지)
    contextService.startNewConversation();
    setIsAIAssistantOpen(false);
    setIsSidebarChatOpen(false);
    // 문서 내용은 그대로 유지
  };

  const onOpenRecentFiles = () => {
    if (isPreviewGuest) return; // 미리보기에서는 최근 파일 목록도 열지 않음
    setIsRecentFilesOpen(true);
  };

  const onOpenHelpModal = () => {
    setIsHelpModalOpen(true);
  };

  const onCloseHelpModal = () => {
    setIsHelpModalOpen(false);
  };

  const onOpenTutorial = () => {
    setIsTutorialOpen(true);
  };

  const onCloseTutorial = () => {
    setIsTutorialOpen(false);
  };

  const onTutorialComplete = () => {
    localStorage.setItem('tutorialCompleted', 'true');
    setIsTutorialOpen(false);
  };

  const onLoadFile = (fileContent) => {
    // 히스토리 스냅샷
    undoStackRef.current.push(content);
    redoStackRef.current = [];
    setContent(fileContent);
    contextService.updateDocumentContent(fileContent);
  };

  // 관리자 모드일 때는 관리자 대시보드만 렌더링
  if (isAdminOpen) {
    return (
      <ThemeProvider>
        <AdminDashboard onClose={() => setIsAdminOpen(false)} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AppContainer>
        <Header 
          currentFile={currentFile}
          onSave={handleSave}
          onToggleAI={onToggleAIAssistant}
          onToggleSidebarChat={onToggleSidebarChat}
          onNewConversation={onNewConversation}
          onOpenRecentFiles={onOpenRecentFiles}
          onOpenAPISettings={() => setIsAPISettingsOpen(true)}
          onOpenLogin={() => setIsLoginOpen(true)}
        />
        <MainContent>
          <Sidebar 
            currentFile={currentFile}
            onNewDocument={() => {
              setCurrentFile(null);
              setContent('');
            }}
            onSave={handleSave}
            onOpenFile={handleOpenFile}
            onToggleAI={handleToggleAI}
            onOpenHelp={onOpenHelpModal}
            onOpenTutorial={onOpenTutorial}
            onOpenLogin={() => setIsLoginOpen(true)}
            onOpenAPISettings={() => setIsAPISettingsOpen(true)}
            onOpenAdmin={() => setIsAdminOpen(true)}
            aiMode={aiMode}
            account={account}
            setAccount={setAccount}
            onOpenAccount={() => setIsAccountOpen(true)}
            onOpenCharge={() => setIsChargeOpen(true)}
            recentFiles={recentFiles.map(f => f.name)}
          />
          <ContentArea>
            <WordEditor 
              content={content}
              onChange={handleContentChange}
            onSave={handleSave}
            onManualSave={handleManualSave}
              previewContent={previewContent}
              previewMode={previewMode}
              onApplyPreview={handleApplyPreview}
              onRejectPreview={handleRejectPreview}
              onStartChat={handleStartChat}
              readOnly={isPreviewGuest}
            />
          </ContentArea>
          {isSidebarChatOpen && (
            <SidebarChat
              isOpen={isSidebarChatOpen}
              onClose={onCloseSidebarChat}
              mode={aiMode}
              onModeChange={onModeChange}
              content={previewContent && previewMode ? previewContent : content}
              selectedText={selectedText}
              onResponse={handleAIResponse}
              onMinimize={onMinimizeSidebarChat}
              isMinimized={isSidebarChatMinimized}
            />
          )}
        </MainContent>

        {pdfProgress.active && (
          <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 2000, width: 280, background: '#0b1220', color: '#e5e7eb', border: '1px solid #1f2a44', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,.35)' }}>
            <div style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600 }}>PDF 내보내는 중...</div>
            <div style={{ padding: '0 12px 12px 12px' }}>
              <div style={{ height: 6, background: '#1f2a44', borderRadius: 999 }}>
                <div style={{ height: 6, width: `${Math.max(2, Math.min(100, Math.floor(pdfProgress.percent)))}%`, background: 'linear-gradient(90deg,#0A84FF,#7C3AED)', borderRadius: 999, transition: 'width .2s ease' }} />
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: '#9fb0d0' }}>{pdfProgress.message || `${pdfProgress.percent}%`}</div>
            </div>
          </div>
        )}
        
        <AnimatePresence>
          {isAIAssistantOpen && (
            <AIAssistant 
              mode={aiMode}
              content={content}
              onResponse={handleAIResponse}
              onClose={() => setIsAIAssistantOpen(false)}
            />
          )}
          
          {isAPISettingsOpen && (
            <APISettings 
              isOpen={isAPISettingsOpen}
              onClose={() => setIsAPISettingsOpen(false)}
            />
          )}
          {isAccountOpen && (
            <LoginModal
              isOpen={isAccountOpen}
              onClose={() => setIsAccountOpen(false)}
            onLoggedIn={() => {
              const s = authService.getSession();
              if (s?.user?.email) {
                (async () => {
                  try {
                    const userData = await supabaseService.getUserData(s.user.email);
                    const next = {
                      ...account,
                      email: s.user.email,
                      name: userData?.name || account?.name,
                      points: typeof userData?.points === 'number' ? userData.points : pointsService.getCurrentBalance(),
                      avatarChar: userData?.avatar_char || localStorage.getItem('olp_avatar_char') || '🧠',
                      avatarColor: userData?.avatar_color || localStorage.getItem('olp_avatar_color') || '#7C3AED'
                    };
                    setAccount(next);
                    localStorage.setItem('ai_agent_account', JSON.stringify(next));
                  } catch {
                    const next = { ...account, email: s.user.email };
                    setAccount(next);
                    localStorage.setItem('ai_agent_account', JSON.stringify(next));
                  }
                })();
              }
            }}
            onProfileUpdated={(partial) => {
              setAccount(prev => ({ ...prev, ...partial }));
              const cur = JSON.parse(localStorage.getItem('ai_agent_account')||'{}');
              localStorage.setItem('ai_agent_account', JSON.stringify({ ...cur, ...partial }));
            }}
            />
          )}
          {isChargeOpen && (
            <ChargeModal
              isOpen={isChargeOpen}
              onClose={() => setIsChargeOpen(false)}
              onCharged={(bal) => setAccount(prev => ({ ...prev, points: bal }))}
            />
          )}

          {isAdminOpen && (
            <AdminModal
              isOpen={isAdminOpen}
              onClose={() => setIsAdminOpen(false)}
            />
          )}

          {isLoginOpen && (
            <LoginModal
              isOpen={isLoginOpen}
              onClose={() => setIsLoginOpen(false)}
              onLoggedIn={() => {
                // 계정 동기화
                const s = authService.getSession();
                if (s?.user?.email) {
                  (async () => {
                    try {
                      const userData = await supabaseService.getUserData(s.user.email);
                      console.log('App.js - 로그인 후 사용자 데이터:', userData);
                      
                      // Supabase에서 가져온 실제 포인트 사용 (localStorage 완전 무시)
                      const actualPoints = typeof userData?.points === 'number' ? userData.points : 1500;
                      
                      console.log('App.js - Supabase에서 가져온 실제 포인트:', actualPoints);
                      console.log('App.js - 기존 localStorage 포인트:', pointsService.getCurrentBalance());
                      
                      // localStorage 완전 초기화하고 Supabase 포인트만 사용
                      localStorage.setItem('ai_agent_points_v1', String(actualPoints));
                      localStorage.setItem('ai_agent_user_type_v1', 'logged_in');
                      
                      console.log('App.js - localStorage 초기화 후:', pointsService.getCurrentBalance());
                      
                      const next = {
                        ...account,
                        email: s.user.email,
                        name: userData?.name || account?.name,
                        points: actualPoints, // Supabase 실제 포인트 사용
                        avatarChar: userData?.avatar_char || localStorage.getItem('olp_avatar_char') || '🧠',
                        avatarColor: userData?.avatar_color || localStorage.getItem('olp_avatar_color') || '#7C3AED'
                      };
                      
                      console.log('App.js - 동기화된 계정 정보:', next);
                      setAccount(next);
                      localStorage.setItem('ai_agent_account', JSON.stringify(next));
                    } catch (error) {
                      console.error('App.js - 사용자 데이터 동기화 실패:', error);
                      const next = { ...account, email: s.user.email };
                      setAccount(next);
                      localStorage.setItem('ai_agent_account', JSON.stringify(next));
                    }
                  })();
                }
              }}
              onProfileUpdated={(partial) => {
                setAccount(prev => ({ ...prev, ...partial }));
                const cur = JSON.parse(localStorage.getItem('ai_agent_account')||'{}');
                localStorage.setItem('ai_agent_account', JSON.stringify({ ...cur, ...partial }));
              }}
              onLoggedOut={() => {
                console.log('App.js - 로그아웃 처리 시작');
                
                // 게스트 사용자로 복원
                pointsService.setLoggedOut();
                
                setAccount(prev => ({ ...prev, email: undefined }));
                const cur = JSON.parse(localStorage.getItem('ai_agent_account')||'{}');
                delete cur.email; localStorage.setItem('ai_agent_account', JSON.stringify(cur));
                
                console.log('App.js - 로그아웃 처리 완료, 게스트 포인트:', pointsService.getCurrentBalance());
              }}
            />
          )}
          
          {isRecentFilesOpen && (
            <RecentFiles 
              isOpen={isRecentFilesOpen}
              onClose={() => setIsRecentFilesOpen(false)}
              onLoadFile={onLoadFile}
              onSelectSession={(sessionId, openMode) => {
                contextService.setCurrentSession(sessionId);
                if (openMode) setAiMode(openMode);
                setIsSidebarChatOpen(true);
              }}
            />
          )}

          {isHelpModalOpen && (
            <HelpModal 
              isOpen={isHelpModalOpen}
              onClose={onCloseHelpModal}
            />
          )}

          {isTutorialOpen && (
            <TutorialModal 
              isOpen={isTutorialOpen}
              onClose={onCloseTutorial}
              onComplete={onTutorialComplete}
            />
          )}
          {isSaveAsOpen && (
            <SaveAsModal 
              isOpen={isSaveAsOpen}
              onClose={() => setIsSaveAsOpen(false)}
              onSelect={handleSaveAsSelect}
            />
          )}

        </AnimatePresence>

        {/* 실시간 상담: 벤더 런처를 좌하단으로 이동하여 사용 */}
      </AppContainer>
    </ThemeProvider>
  );
}

export default WordApp;
